/**
 * Loading a state's data on demand (registry.ts). `ensureStateLoaded("NY")` resolves when New York's rules can be read
 * synchronously; the web build fetches that state's chunk with a dynamic import, everything else (server, scripts, tests,
 * phone app) already has all states installed by states/eager.ts and resolves at once.
 *
 * A failed load is not remembered: the next call tries again (the app's Retry button). Concurrent calls share one load.
 */
import { installState, isStateLoaded, type StateData } from "./registry.ts";
import { isSupportedState, type UsState } from "./types.ts";

type Loader = () => Promise<{ default: StateData }>;

/** One dynamic import per state: each is its own chunk in the web build. */
const LOADERS: Record<Exclude<UsState, "UT">, Loader> = {
  NV: () => import("./bundles/state-nv.ts"),
  CA: () => import("./bundles/state-ca.ts"),
  NY: () => import("./bundles/state-ny.ts"),
  OR: () => import("./bundles/state-or.ts"),
  FL: () => import("./bundles/state-fl.ts"),
  TX: () => import("./bundles/state-tx.ts"),
  MA: () => import("./bundles/state-ma.ts"),
};

const loaderOf: Partial<Record<UsState, Loader>> = { ...LOADERS };
const IN_FLIGHT = new Map<UsState, Promise<void>>();

/** Tests only: replace how a state loads (a slow or failing chunk), or pass undefined to restore the real one. */
export function setStateLoaderForTest(state: Exclude<UsState, "UT">, load: Loader | undefined): void { loaderOf[state] = load ?? LOADERS[state]; }

/** Resolves when this state's data is readable. Rejects when the chunk could not be fetched; call again to retry. */
export function ensureStateLoaded(state: string | null | undefined): Promise<void> {
  if (!isSupportedState(state) || isStateLoaded(state)) return Promise.resolve();
  const running = IN_FLIGHT.get(state);
  if (running) return running;
  const load = loaderOf[state];
  if (!load) return Promise.reject(new Error(`No loader for ${state}`));
  const p = load().then((m) => { installState(state, m.default); }).finally(() => { IN_FLIGHT.delete(state); });
  IN_FLIGHT.set(state, p);
  return p;
}

/** Several states at once (an organization's locations may sit in more than one). Utah and unknown codes are skipped. */
export function ensureStatesLoaded(states: Iterable<string | null | undefined>): Promise<void> {
  return Promise.all([...new Set(states)].map((s) => ensureStateLoaded(s))).then(() => undefined);
}
